import { Progress as ProgressPrimitive } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/lib/utils';

// shadcn/ui (AD #39): shadcn's Progress, added with the CLI and adapted to the CRM's rules — `cn` from
// `@/lib/utils`, imported from `radix-ui` as every shadcn file here is, no own focus ring (it is not
// focusable), and made determinate (F20-h WI-1.2 plan Decision 25): `value` and `max` reach the Radix root,
// so it exposes `role="progressbar"` with `aria-valuenow` and `aria-valuemax`, and the indicator's travel
// is computed from `value / max` rather than assuming a 0–100 scale. The inline style holds a percentage
// only. The caller names it with `aria-label`.
type ProgressProps = Omit<React.ComponentProps<typeof ProgressPrimitive.Root>, 'value' | 'max'> & {
    /** How much is done, from 0 to `max`. */
    value: number;
    /** The whole; must be above 0. Defaults to 100. */
    max?: number;
};

function Progress({ className, value, max = 100, ...props }: ProgressProps) {
    const whole = max > 0 ? max : 100;
    const done = Math.min(Math.max(value, 0), whole);
    return (
        <ProgressPrimitive.Root
            /* testid-exempt: a shadcn primitive; the caller passes data-testid and aria-label through props */
            data-slot="progress"
            className={cn('relative h-2 w-full overflow-hidden rounded-full bg-primary/20', className)}
            value={done}
            max={whole}
            {...props}
        >
            <ProgressPrimitive.Indicator
                data-slot="progress-indicator"
                className="h-full w-full flex-1 bg-primary transition-all motion-reduce:transition-none"
                style={{ transform: `translateX(-${100 - (done / whole) * 100}%)` }}
            />
        </ProgressPrimitive.Root>
    );
}

export { Progress };
